import ChatBot16 from '@carbon/icons-react/es/chat-bot/16';
import Send16 from '@carbon/icons-react/es/send/16';
import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import { Popover } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Project, ProjectAPI } from 'api/project';
import { Field } from 'api/tasks';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Tag } from 'components/atoms/tag';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { RoutePaths } from 'constant';
import { useProjectFieldsQuery } from 'queries';
import { logger } from 'services';
import { getRandomColor } from 'utils/get-random-color';
import { queueMacrotask } from 'utils/macrotask';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 600px;
  width: 400px;
  padding: var(--spacing-05);
  background: var(--white-100);
  overflow-x: hidden;
`;

const StyledForm = styled(Form)`
  .ant-row.ant-form-item {
    padding-bottom: 0 !important;
  }
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
  .ant-form-item {
    padding-bottom: 0 !important;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  [data-cy='select.basic'] {
    width: 100% !important;
  }
`;

const TagsWrapper = styled.div`
  display: flex;
  margin: var(--spacing-04) 0;
  flex-wrap: wrap;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const StyledEmptyStateContainer = styled(EmptyStateContainer)`
  height: 100%;
  padding: 0 !important;
`;

const ProjectWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: var(--spacing-07);
`;

const ProjectName = styled.p`
  margin-left: var(--spacing-03);
  margin-bottom: 0;
`;

const ListWrapper = styled.div`
  margin-top: var(--spacing-06);
  margin-bottom: var(--spacing-06);
  overflow-y: auto;
  height: 100%;
`;

const StyledLink = styled(Link)`
  color: var(--gray-100);
  :hover {
    color: black;
  }
`;

const ProjectItem = ({
  project,
  statusField,
}: {
  project: Project;
  statusField: Field;
}) => {
  const value = project?.fields?.find(
    (e) => e.fieldId === statusField?.fieldId,
  )?.fieldValue;

  const currentStatus = statusField?.metaData?.options?.find(
    (e) => e.value === value,
  );

  return (
    <StyledLink
      to={RoutePaths.projectDetail.replaceAll(
        ':projectId',
        project.projectId.toString(),
      )}
    >
      <ProjectWrapper>
        {currentStatus ? (
          <StatusIndicator status={currentStatus} />
        ) : (
          <StatusIndicator
            status={{ color: 'blue', group: 1, label: 'To do' }}
          />
        )}
        <ProjectName>{project.projectName}</ProjectName>
      </ProjectWrapper>
    </StyledLink>
  );
};

const SUGGESSTIONS = [
  'Which projects which went live last month?',
  'Show me the list of blocked projects',
  'Which projects are starting next month?',
  'Show me the projects where the project owner is X',
];

export const GptPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onSendClick = async () => {
    if (form.getFieldValue('input')) {
      setLoading(true);
      queueMacrotask(async () => {
        try {
          setProjects(
            await ProjectAPI.gpt({
              input: form.getFieldValue('input'),
            }),
          );
        } catch (error) {
          logger.error('GPT Failed', { error: new Error() });
        }
        setLoading(false);
      });
    }
  };

  const { data } = useProjectFieldsQuery();

  const statusField = data?.find((e) => e.fieldName === 'Status');

  const Portal = () => (
    <ContentWrapper>
      {projects.length > 0 ? (
        <ListWrapper>
          {projects.map((e) =>
            statusField ? (
              <ProjectItem project={e} statusField={statusField} />
            ) : null,
          )}
        </ListWrapper>
      ) : (
        <StyledEmptyStateContainer>
          <EmptyState
            image={<EmptyStateIcon icon={faTelescope} />}
            title={<FormattedMessage id="projects.no_results.empty.title" />}
            message={
              <FormattedMessage id="projects.no_results.empty.message" />
            }
            secondaryCTA={<></>}
          />
        </StyledEmptyStateContainer>
      )}
      <InputWrapper>
        <StyledFormItem name="input">
          <StyledInput name="input" />
        </StyledFormItem>

        <Button
          disabled={isLoading}
          icon={isLoading ? <Loading /> : <Send16 />}
          onClick={onSendClick}
        />
      </InputWrapper>
      <TagsWrapper>
        {SUGGESSTIONS.map((e) => (
          <Tag
            color={getRandomColor(e)}
            onClick={() => {
              form.setFieldsValue({
                input: e,
              });
            }}
          >
            {e}
          </Tag>
        ))}
      </TagsWrapper>
    </ContentWrapper>
  );

  return (
    <Popover
      content={
        <StyledForm form={form}>
          <Portal />
        </StyledForm>
      }
      trigger={['click']}
      placement="leftTop"
    >
      <Button icon={<ChatBot16 />} />
    </Popover>
  );
};
